import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Lightbox } from '@/components/Lightbox';
import { AvatarChip } from '@/components/AvatarChip';
import { PostThumb, PostMediaBlock, usePostMedia } from '@/features/feed/PostMediaBlock';
import { ReportDialog } from '@/features/report/ReportDialog';
import { container } from '@/services/container';
import { useFeedStore } from '@/store/feed';
import { useSocialStore } from '@/store/social';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import type { Post } from '@shared/types';

/** did:dpb:0x… → 0x… 地址（链上关注/订阅目标）。 */
function addressFromDid(did: string): string {
  const s = String(did ?? '').trim();
  return s.startsWith('did:dpb:') ? s.slice('did:dpb:'.length).toLowerCase() : '';
}

const HIDDEN_KEY = 'mesh_hidden_posts';

function loadHidden(): Set<string> {
  try {
    const raw = window.localStorage.getItem(HIDDEN_KEY);
    const list = raw ? (JSON.parse(raw) as string[]) : [];
    return new Set(Array.isArray(list) ? list : []);
  } catch {
    return new Set();
  }
}

function saveHidden(set: Set<string>): void {
  try {
    window.localStorage.setItem(HIDDEN_KEY, JSON.stringify([...set]));
  } catch {
    /* 存储满忽略 */
  }
}

/**
 * v3.19 自媒体：创作者主页（/u/:did）。
 *
 * 聚合该创作者在广场的全部发布 + 链上粉丝数 + 关注/订阅操作；
 * 本人主页额外提供「隐藏发布」（本地管理，链上存证保留）。
 */
export function ProfilePage(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ did: string }>();
  const rawDid = decodeURIComponent(params.did ?? '');
  const did = rawDid.startsWith('did:dpb:') ? rawDid : `did:dpb:${rawDid}`;
  const address = addressFromDid(did);

  const posts = useFeedStore((s) => s.posts);
  const stats = useFeedStore((s) => s.stats);
  const following = useSocialStore((s) => s.following);
  const followerCount = useSocialStore((s) => s.followerCounts[did] ?? 0);
  const selfDid = useIdentityStore((s) => s.did);
  const selfAddr = useIdentityStore((s) => s.address);
  const isSelf = !!selfDid && selfDid.toLowerCase() === did.toLowerCase();

  const [hidden, setHidden] = useState<Set<string>>(() => loadHidden());
  const [busy, setBusy] = useState<string | null>(null);
  const [previewCid, setPreviewCid] = useState<string | null>(null);
  const [reportTarget, setReportTarget] = useState<string | null>(null);
  const [followingCount, setFollowingCount] = useState(0);
  const { url: previewUrl } = usePostMedia(previewCid ?? '');

  /** 该创作者发布列表（过滤本人隐藏项）。 */
  const myPosts = useMemo(() => {
    const d = did.toLowerCase();
    return posts
      .filter((p) => typeof p.authorDid === 'string' && p.authorDid.toLowerCase() === d && !hidden.has(p.id))
      .sort((a, b) => b.ts - a.ts);
  }, [posts, did, hidden]);

  /** 创作者展示名：优先帖子名，回落地址短码。 */
  const displayName = useMemo(() => {
    for (const p of myPosts) {
      const n = typeof p.authorName === 'string' && p.authorName ? p.authorName : '';
      if (n) return n;
    }
    return address ? `${address.slice(0, 10)}…` : '未知创作者';
  }, [myPosts, address]);

  const bio = useMemo(() => {
    const all = posts.filter((p) => typeof p.authorDid === 'string' && p.authorDid.toLowerCase() === did.toLowerCase());
    return all.length > 0 ? `${all.length} 条发布，内容已上链存证` : '';
  }, [posts, did]);

  useEffect(() => {
    if (!address) return;
    void container.social.getFollowerCount(did).catch(() => undefined);
    if (isSelf) {
      void container.social.getFollowing().then((l) => setFollowingCount(l.length)).catch(() => undefined);
    }
  }, [address, did, isSelf]);

  const isFollowing = following.includes(did);

  /** 关注 / 订阅创作者（社交动作上链 → 行为挖矿）。 */
  const engage = async (action: 'follow' | 'subscribe'): Promise<void> => {
    const key = `${action}:${address}`;
    if (busy || !address) return;
    if (!selfAddr) {
      useUiStore.getState().pushError('E_LOCKED');
      return;
    }
    setBusy(key);
    try {
      if (action === 'follow') {
        if (isFollowing) {
          await container.social.unfollow(did);
        } else {
          await container.social.follow(did);
        }
        useUiStore.getState().pushToast('success', isFollowing ? '已取消关注' : `已关注 ${displayName}`);
      } else {
        await container.chain.socialAct({
          action,
          from: selfAddr as `0x${string}`,
          target: address as `0x${string}`,
          signature: container.identity.signObject({ action, from: selfAddr, target: address, ts: Date.now() }),
        });
        useUiStore.getState().pushToast('success', `已订阅 ${displayName}（上链）`);
      }
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(null);
    }
  };

  /** 本人隐藏发布（本地管理；链上存证按不可篡改原则保留）。 */
  const hidePost = (id: string): void => {
    const next = new Set(hidden);
    next.add(id);
    setHidden(next);
    saveHidden(next);
    useUiStore.getState().pushToast('success', '已从本人主页隐藏（链上存证保留）');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          ← 返回
        </Button>
        <p className="text-[11px] text-dim">创作者主页</p>
      </div>

      {/* 资料卡 */}
      <section className="card p-5">
        <div className="flex items-start gap-4">
          <AvatarChip did={did} name={displayName} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold text-text">{displayName}</p>
            <p className="mono mt-0.5 truncate text-[11px] text-dim">{did}</p>
            <p className="mt-2 text-xs text-dim">{bio || '暂无简介'}</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Badge tone="neutral">发布 {myPosts.length}</Badge>
          <Badge tone="neutral">粉丝 {followerCount}</Badge>
          {isSelf ? <Badge tone="neutral">关注 {followingCount}</Badge> : null}
          {isSelf ? <Badge tone="ok">我的主页</Badge> : null}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {isSelf ? (
            <>
              <Button size="sm" onClick={() => navigate('/me')}>
                编辑资料与头像
              </Button>
              <Button size="sm" variant="ghost" onClick={() => navigate('/feed')}>
                去发布
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                loading={busy === `follow:${address}`}
                onClick={() => void engage('follow')}
              >
                {isFollowing ? '已关注 ✓' : '关注（上链）'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={busy === `subscribe:${address}`}
                onClick={() => void engage('subscribe')}
              >
                订阅
              </Button>
            </>
          )}
        </div>
      </section>

      {/* 发布列表 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">
          {isSelf ? '我的发布' : '发布'} <span className="text-[11px] text-dim">({myPosts.length})</span>
        </h2>
        {myPosts.length === 0 ? (
          <EmptyState title="还没有发布" description={isSelf ? '去广场发第一条内容（发文即挖矿）' : '该创作者暂未发布内容'} />
        ) : (
          <ul className="space-y-3">
            {myPosts.map((p) => (
              <ProfilePostRow
                key={p.id}
                post={p}
                stat={stats[p.id] ?? {}}
                isSelf={isSelf}
                onPreview={setPreviewCid}
                onReport={setReportTarget}
                onHide={hidePost}
              />
            ))}
          </ul>
        )}
      </section>

      {previewUrl ? <Lightbox url={previewUrl} onClose={() => setPreviewCid(null)} /> : null}
      <ReportDialog
        open={!!reportTarget}
        onClose={() => setReportTarget(null)}
        defaultTargetType="post"
        defaultTargetId={reportTarget ?? undefined}
      />
    </div>
  );
}

/** 紧凑发布行：正文 + 图片/媒体 + 互动统计 + 举报/隐藏。 */
function ProfilePostRow({
  post,
  stat,
  isSelf,
  onPreview,
  onReport,
  onHide,
}: {
  post: Post;
  stat: Record<string, number>;
  isSelf: boolean;
  onPreview: (cid: string) => void;
  onReport: (id: string) => void;
  onHide: (id: string) => void;
}): JSX.Element {
  const images = Array.isArray(post.images) ? post.images : [];
  const media = post.media;
  return (
    <li className="rounded-btn border border-line bg-panel/60 p-3">
      <p className="text-[11px] text-dim">{new Date(post.ts).toLocaleString()}</p>
      {post.text ? <p className="mt-1 whitespace-pre-wrap text-sm text-text">{post.text}</p> : null}
      {images.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {images.map((img) => (
            <PostThumb key={img.cid} cid={img.thumbCid || img.cid} fullCid={img.cid} onPreview={onPreview} />
          ))}
        </div>
      ) : null}
      {media ? <PostMediaBlock media={media} /> : null}
      <p className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-dim">
        {stat.views ? <span>👁 {stat.views}</span> : null}
        {stat.likes ? <span>❤️ {stat.likes}</span> : null}
        {stat.reposts ? <span>🔁 {stat.reposts}</span> : null}
        {stat.comments ? <span>💬 {stat.comments}</span> : null}
        {post.blockHeight ? <span className="mono">#{post.blockHeight}</span> : null}
        <span className="ml-auto flex gap-2">
          <button type="button" className="text-dim hover:text-accent" onClick={() => onReport(post.id)}>
            举报
          </button>
          {isSelf ? (
            <button type="button" className="text-dim hover:text-err" onClick={() => onHide(post.id)}>
              隐藏
            </button>
          ) : null}
        </span>
      </p>
    </li>
  );
}

export default ProfilePage;
